import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CustomDropdown from '@components/CustomDropdown';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import useDocumentTitle from '@hooks/useDocumentTitle';

import { fetchCategories, fetchDepartments, fetchEnumValues, fetchTeamBrands } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { emailRegex } from '@utils/Util';

const AddClient = () => {
	const [state, setState] = useState({
		name: '',
		nickName: '',
		password: '',
		passwordRe: '',
		email: '',
		status: '',
		role: '',
		managerId: '',
		executiveId: '',
		brandId: '',
		brandName: '',
		nameError: '',
		passwordError: '',
		passwordReError: '',
		brandError: '',
		marketError: '',
		emailError: '',
		statusError: '',
		roleError: '',
		departmentIdError: '',
		executiveIdError: '',
		detailedName: '',
		designation: '',
		brandDepartment: '',
		company: '',
		phone: '',
		address: '',
		linkedin: '',
		facebook: '',
		twitter: '',
		passStrengthObj: {
			percentage: '0',
			color: '',
			text: ''
		},
		markets: [],
		selectedMarket: [],
		categories: [],
		selectedCategories: [],
		statusList: [],
		managerList: [],
		executiveList: [],
		brandList: [],
		suggestions: [],
		departments: [],
		teamBrands: [],
		brandTeamId: null,
		showSuggestion: false,
		loading: false
	});
	const clientInfoRef = useRef(null);
	const wrapperRef = useRef(null);
	const [errors, setErrors] = useState({});
	const navigate = useNavigate();

	useDocumentTitle('Add client - Nitex - The easiest clothing manufacturing software');

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);

		(async () => {
			await setData();
			const marketData = await fetchDepartments();
			const categoriesData = await fetchCategories();
			const departmentsData = await fetchEnumValues('info.nitex.app.user.BrandDepartment?sorted=true');
			setState((prevState) => ({
				...prevState,
				markets: marketData?.map((item) => ({ label: item.name, value: item.id })),
				categories: categoriesData?.map((item) => ({ label: item.name, value: item.id })),
				departments: departmentsData || []
			}));

			const { data } = await ApiClient.GET('getBrandBasicList', '?page=0&size=1000');
			setState((prevState) => ({
				...prevState,
				brandList: data.data,
				status: 'ACTIVE'
			}));
		})();

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleClickOutside = (event) => {
		if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
			setState((prevState) => ({ ...prevState, showSuggestion: false }));
		}
	};

	const fetchSuggestions = async () => {
		try {
			const { data } = await ApiClient.GET('getCompanyList', `?name=${state.company}`);
			setState((prevState) => ({
				...prevState,
				suggestions: data,
				companyId: data.length ? prevState.companyId : ''
			}));
		} catch (error) {
			toastError(error.response?.data?.message || 'Something went wrong! Please try again.');
			setState((prevState) => ({ ...prevState, loading: false }));
		}
	};

	const setData = async () => {
		try {
			const { data: statusData } = await ApiClient.GET('getStatus');
			const { data: managerData } = await ApiClient.GET('getManagers');
			const { data: executiveData } = await ApiClient.GET('getExecutives');

			setState((prevState) => ({
				...prevState,
				statusList: statusData,
				managerList: managerData,
				executiveList: executiveData.concat(managerData)
			}));
		} catch (error) {
			toastError(error.response?.data?.message || 'Something went wrong! Please try again.');
			setState((prevState) => ({ ...prevState, loading: false }));
		}
	};

	const onChange = (e) => {
		const { name, value } = e.target;
		if (name === 'password') setPassPercentage(value);
		if (name === 'company') fetchSuggestions();
		setState((prevState) => ({
			...prevState,
			[name]: value,
			...(name === 'company' && { companyId: '' })
		}));
		resetValidation(name, value);
	};

	const setPassPercentage = (val) => {
		let percentage = 0;
		if (/\d/.test(val)) percentage += 25;
		if (/(?=.*[A-Z])/.test(val)) percentage += 25;
		if (val.length > 5) percentage += 50;

		let passStrengthObj = {};
		if (percentage < 50) {
			passStrengthObj = { percentage, color: 'red', text: 'Weak' };
		} else if (percentage < 75) {
			passStrengthObj = { percentage, color: 'yellow', text: 'Medium' };
		} else {
			passStrengthObj = { percentage, color: 'green', text: 'Strong' };
		}
		setState((prevState) => ({ ...prevState, passStrengthObj }));
	};

	const resetValidation = (fieldName, value) => {
		if (value) {
			switch (fieldName) {
				case 'email':
					removeErrorFlag('emailError');
					break;
				case 'name':
					removeErrorFlag('nameError');
					break;
				case 'brandId':
					removeErrorFlag('brandError');
					break;
				case 'password':
					removeErrorFlag('passwordError');
					break;
				case 'passwordRe':
					removeErrorFlag('passwordReError');
					break;
				case 'status':
					removeErrorFlag('statusError');
					break;
				case 'departmentId':
					removeErrorFlag('departmentIdError');
					break;
				case 'executiveId':
					removeErrorFlag('executiveIdError');
					break;
				default:
					break;
			}
		}
	};

	const removeErrorFlag = (flagName) => {
		setState((prevState) => ({ ...prevState, [flagName]: '' }));
	};

	const validate = () => {
		let errorObj = {};
		let { name, password, passwordRe, email, status, brandDepartment, executiveId, brandId, nickName } =
			state;
		let re = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}/;

		if (email === '') {
			errorObj.emailError = 'Email is required!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -890);
		} else if (!emailRegex.test(email)) {
			errorObj.emailError = 'Invalid email format!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -890);
		}

		if (!name) {
			errorObj.nameError = 'Name is required!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -850);
		}
		if (!nickName) {
			errorObj.nickNameError = 'Nick name is required!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -850);
		}

		if (!brandId) {
			errorObj.brandError = 'Brand is required!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -800);
		}

		if (password === '') {
			errorObj.passwordError = 'Password is required!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -800);
		} else if (!re.test(password)) {
			errorObj.passwordError =
				'Password must contain 6 characters, at least one number, one lowercase and one uppercase letter';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -800);
		}

		if (passwordRe !== password) {
			errorObj.passwordReError = "Passwords didn't match!";
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -800);
		}

		if (!status) {
			errorObj.statusError = 'Status is required!';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -450);
		}

		if (!brandDepartment) {
			errorObj.departmentIdError = 'Please select a department';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -0);
		}

		if (!executiveId) {
			errorObj.executiveIdError = 'Please select an executive';
			clientInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			window.scrollBy(0, -450);
		}
		setErrors(errorObj);
		return Object.keys(errorObj).length === 0;
	};

	const submit = async () => {
		if (!validate()) return;

		setState((prevState) => ({ ...prevState, loading: true }));
		let {
			name,
			email,
			password,
			passwordRe,
			status,
			managerId,
			executiveId,
			brandId,
			nickName,
			designation,
			brandDepartment
		} = state;
		const body = {
			fullName: name,
			email,
			password,
			retypePassword: passwordRe,
			status,
			managerId,
			executiveId,
			brandId,
			nickName,
			designation,
			brandDepartment
		};
		try {
			const { data } = await ApiClient.POST('addClient', body);
			toastSuccess(data.message);
			navigate('/clients/list');
		} catch (error) {
			toastError(error.response?.data?.message || 'Something went wrong! Please try again.');
		} finally {
			setState((prevState) => ({ ...prevState, loading: false }));
		}
	};

	const onItemClick = async (item) => {
		const teamBrandsData = await fetchTeamBrands(item.id);
		setState((prev) => ({
			...prev,
			brandId: item.id,
			brandName: item.value,
			teamBrands: teamBrandsData || []
		}));

		resetValidation('brandId', item.id); // Assuming resetValidation is defined in the component
	};

	const reset = () => {
		setState({
			name: '',
			nickName: '',
			password: '',
			passwordRe: '',
			email: '',
			status: '',
			role: '',
			managerId: '',
			executiveId: '',
			brandId: '',
			brandName: '',
			nameError: '',
			passwordError: '',
			passwordReError: '',
			brandError: '',
			marketError: '',
			emailError: '',
			statusError: '',
			roleError: '',
			departmentIdError: '',
			executiveIdError: '',
			detailedName: '',
			designation: '',
			brandDepartment: '',
			company: '',
			phone: '',
			address: '',
			linkedin: '',
			facebook: '',
			twitter: '',
			passStrengthObj: {
				percentage: '0',
				color: '',
				text: ''
			},
			markets: [],
			selectedMarket: [],
			categories: [],
			selectedCategories: [],
			statusList: [],
			managerList: [],
			executiveList: [],
			brandList: [],
			suggestions: [],
			departments: [],
			teamBrands: [],
			brandTeamId: null,
			showSuggestion: false,
			loading: false
		});
	};

	return (
		<LoaderComponent loading={state.loading}>
			<section className="project-profile">
				<div className="new-collection mt-4 add-client">
					<div className="new-collection-form pt-0" ref={clientInfoRef}>
						<div className="form-header pb-3">
							<p className="semibold-16 mb-0">Add buyer</p>
						</div>
						<div className="row">
							<div className="col-lg-12 mb-2">
								<h3 className="semibold-16">Buyer Info</h3>
							</div>
							{/* Full Name Input */}
							<div className="col-lg-6">
								<div className="form-group">
									<label>Full name*</label>
									<input
										className="bg-gray-light border-0"
										type="text"
										autoComplete="off"
										name="name"
										value={state.name}
										onChange={onChange}
										placeholder="Enter full name"
									/>
									{errors.nameError && <span className="error">{errors.nameError}</span>}
								</div>
							</div>

							{/* Nickname Input */}
							<div className="col-lg-6">
								<div className="form-group">
									<label>Nickname*</label>
									<input
										className="bg-gray-light border-0"
										type="text"
										autoComplete="off"
										name="nickName"
										value={state.nickName}
										onChange={onChange}
										placeholder="Enter nickname"
									/>
									{errors.nickNameError && (
										<span className="error">{errors.nickNameError}</span>
									)}
								</div>
							</div>

							{/* Email Input */}
							<div className="col-lg-6">
								<div className="form-group">
									<label>Email*</label>
									<input
										className="bg-gray-light border-0 mail"
										type="email"
										name="email"
										autoComplete="off"
										value={state.email}
										onChange={onChange}
										placeholder="Enter email address"
									/>
									{errors.emailError && <span className="error">{errors.emailError}</span>}
								</div>
							</div>

							{/* Phone Number Input */}
							<div className="col-lg-6">
								<div className="form-group">
									<label>Phone number</label>
									<input
										className="bg-gray-light border-0"
										type="text"
										autoComplete="off"
										placeholder="Enter phone number"
										name="phone"
										value={state.phone}
										onChange={onChange}
									/>
								</div>
							</div>

							{/* LinkedIn Profile Input */}
							<div className="col-lg-12">
								<div className="form-group mb-0">
									<div className="input-group">
										<label>LinkedIn profile</label>
										<input
											className="form-control bg-gray-light border-0"
											type="text"
											autoComplete="off"
											placeholder="Enter LinkedIn profile"
											name="linkedin"
											value={state.linkedin}
											onChange={onChange}
										/>
									</div>
								</div>
							</div>

							{/* Brand Dropdown */}
							<div className="col-lg-12">
								<div className="form-group">
									<label>Brand*</label>
									<CustomDropdown
										type="addItem"
										isAddNew={false}
										onItemClick={onItemClick}
										items={state.brandList?.map((item) => ({
											value: item.name,
											id: item.id
										}))}
										selectedItem={state.brandName}
									/>
									{errors.brandError && <span className="error">{errors.brandError}</span>}
								</div>
							</div>
							<div className="col-lg-6">
								<div className="form-group">
									<label>Department*</label>
									<select
										name="brandDepartment"
										value={state.brandDepartment}
										onChange={onChange}
										className="bg-gray-light"
									>
										<option>Select department</option>
										{state.departments?.map((item) => (
											<option key={item.id} value={item.constant}>
												{item.name}
											</option>
										))}
									</select>
									{errors.departmentIdError && (
										<span className="error">{errors.departmentIdError}</span>
									)}
								</div>
							</div>
							<div className="col-lg-6">
								<div className="form-group">
									<label>Designation</label>
									<input
										className="bg-gray-light border-0"
										type="text"
										autoComplete="off"
										placeholder="Enter designation"
										name="designation"
										value={state.designation}
										onChange={onChange}
									/>
								</div>
							</div>

							<div className="col-lg-12">
								<div className="form-group mb-0">
									<label>Address</label>
									<textarea
										className="bg-gray-light border-0"
										cols="30"
										rows="10"
										placeholder="Enter address..."
										name="address"
										value={state.address}
										onChange={onChange}
									/>
								</div>
							</div>
						</div>

						{/* Admin Activity Section */}
						<div className="row">
							<div className="col-lg-12">
								<h4 className="semibold-16">Admin Activity</h4>
							</div>
							{/* Status Select */}
							<div className="col-lg-6">
								<div className="form-group">
									<label>Status*</label>
									<select
										name="status"
										value={state.status}
										onChange={onChange}
										className="bg-gray-light"
									>
										{state.statusList.map((item) => (
											<option key={item.key} value={item.key}>
												{item.value}
											</option>
										))}
									</select>
									{errors.statusError && (
										<span className="error">{errors.statusError}</span>
									)}
								</div>
							</div>

							{/* Account Manager Select */}
							<div className="col-lg-6">
								<div className="form-group mb-0">
									<label>Account manager*</label>
									<select
										name="executiveId"
										value={state.executiveId}
										onChange={onChange}
										className="bg-gray-light"
									>
										<option>Select manager</option>
										{state.executiveList.map((item) => (
											<option key={item.id} value={item.id}>
												{item.name}
											</option>
										))}
									</select>
									{errors.executiveIdError && (
										<span className="error">{errors.executiveIdError}</span>
									)}
								</div>
							</div>
							<div className="col-lg-12">
								<p>Create password for your buyer*</p>
							</div>
							<div className="col-lg-6">
								<div className="form-group mb-2">
									<label>Password*</label>
									<input
										className="bg-gray-light border-0"
										type="password"
										name="password"
										value={state.password}
										autoComplete="new-password"
										onChange={onChange}
										placeholder="Enter Password"
									/>
									{errors.passwordError && (
										<span className="error">{errors.passwordError}</span>
									)}
								</div>
							</div>
							<div className="col-lg-6">
								<div className="form-group">
									<label>Confirm password*</label>
									<input
										className="bg-gray-light border-0"
										type="password"
										name="passwordRe"
										value={state.passwordRe}
										onChange={onChange}
										placeholder="Enter Password"
									/>
									{errors.passwordReError && (
										<span className="error">{errors.passwordReError}</span>
									)}
								</div>
							</div>
							<div className="col-lg-12">
								<ul className="password-hint mb-3">
									<li style={{ fontSize: '12px' }}>
										* At least 6 characters & one number, uppercase and lowercase
									</li>
								</ul>
								<div className="guide">
									<div className="bar">
										<div className="progress">
											<div
												className="progress-bar"
												role="progressbar"
												style={{
													width: `${state.passStrengthObj.percentage}%`,
													backgroundColor: state.passStrengthObj.color
												}}
												aria-valuenow="75"
												aria-valuemin="0"
												aria-valuemax="100"
											></div>
										</div>
									</div>
									<p className="password-hint" style={{ fontSize: '14px' }}>
										{state.passStrengthObj.text}
									</p>
								</div>
							</div>
						</div>

						<div className="d-flex  justify-content-end mt-0">
							<button className="button gray-outline mr-2" onClick={reset}>
								Reset
							</button>
							<button className="button" onClick={submit}>
								Submit
							</button>
						</div>
					</div>
				</div>
			</section>
		</LoaderComponent>
	);
};

export default AddClient;
